import * as z from "zod/v4";

export type Booking = z.infer<typeof bookingParser>;
export type AdditionalBooking = z.infer<typeof additionalBookingParser>;
export type SubmittedAdditionalBooking = z.infer<typeof submittedAdditionalBookingParser>;
export type CustomerPreOrder = z.infer<typeof customerPreOrderParser>;
export type CustomerPreOrderUpdateRequest = z.infer<typeof updateCustomerPreOrderItems>;
export interface CustomerPreOrderItemUpdateRequest {
  booking_id: string;
  pendingPreorderId: string | null;
  itemId: string;
  amount: number;
  menuId: string;
  selectedOptions?: { [optionLabel: string]: { [valueId: string]: boolean } };
  multipleNames?: boolean;
  forNames?: string[];
  orderingFor?: number;
  reassignExisting?: boolean;
  booking_type?: string;
  pending?: boolean;
  dietaryRequirements?: Array<{
    name?: string;
    diet_types?: string[];
    allergies?: string[];
    notes?: string;
  }>;
}
export interface RemoveNameItemsRequest {
  booking_id: string;
  pendingPreorderId: string | null;
  nameIndex: number;
  forNames: string[];
  booking_type?: string;
}
export type PreOrderItem = z.infer<typeof preOrderItemParser>;
export type BookingAdditionalType = z.infer<typeof bookingAdditionalTypeParser>;

/**
 * A linked booking type attached to a booking (e.g. Drinks followed by Dinner).
 * Collins returns the type's id alongside its display name and sitting time.
 *
 * `additional_types` was previously untyped, so entries that are not plain objects
 * are coerced to `{}` rather than failing the whole payload.
 */
export const bookingAdditionalTypeParser = z.preprocess(
  (value) => (typeof value === "object" && value !== null && !Array.isArray(value) ? value : {}),
  z.object({
    id: z.string().optional(),
    name: z.string().optional(),
    time: z.string().optional(),
    duration: z.number().optional(),
  }),
);

/* Tax schemas */
export const taxBreakdownEntryParser = z.object({
  label: z.string(),
  amount: z.number(),
});

export const depositTaxRateParser = z.object({
  label: z.string(),
  tax: z.number(),
});

export type TaxBreakdownEntry = z.infer<typeof taxBreakdownEntryParser>;
export type DepositTaxRate = z.infer<typeof depositTaxRateParser>;

/* Shared schemas */
const discountSchema = z.object({
  amount: z.number(),
  quantity: z.number(),
});

const configurableOptionSchema = z.object({
  label: z.string(),
  description: z.string().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  values: z.array(
    z.object({
      label: z.string(),
      price_change: z.number().optional(),
      formattedPriceChange: z.string().optional(),
    }),
  ),
});

/* Base item schema used by food, drink, and other */
const baseMenuItemSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  price: z.number(),
  description: z.string().optional(),
  plu: z.number().optional(),
  discounts: z.array(discountSchema).optional(),
  // Energy content in kilocalories, as configured in Collins. Only some items
  // carry it, and Collins sends `null` for an unset numeric field. Caught
  // rather than merely optional: `handleRpc` answers a failed output parse by
  // returning the whole payload unvalidated, so one odd calorie value would
  // switch off stripping and validation for the entire response.
  calories: z.number().optional().catch(undefined),
});

/* Specific menu item types */
const headingItemSchema = z.object({
  type: z.literal("heading"),
  value: z.string(),
});

const foodItemSchema = baseMenuItemSchema.extend({
  type: z.literal("food"),
  sub_type: z.string().optional(),
  type_id: z.string().optional(),
  sub_type_id: z.string().optional(),
  diet_types: z.array(z.string()).optional(),
  allergens: z.array(z.string()).optional(),
  configurable_options: z.array(configurableOptionSchema).optional(),
  formattedPrice: z.string().optional(),
});

const drinkItemSchema = baseMenuItemSchema.extend({
  type: z.literal("drink"),
  sub_type: z.string().optional(),
  type_id: z.string().optional(),
  sub_type_id: z.string().optional(),
  diet_types: z.array(z.string()).optional(),
  configurable_options: z.array(configurableOptionSchema).optional(),
  formattedPrice: z.string().optional(),
});

const otherItemSchema = baseMenuItemSchema.extend({
  type: z.literal("other"),
  sub_type: z.string().optional(),
  type_id: z.string().optional(),
  sub_type_id: z.string().optional(),
  diet_types: z.array(z.string()).optional(),
  configurable_options: z.array(configurableOptionSchema).optional(),
  formattedPrice: z.string().optional(),
});

/* Discriminated union for menu items */
const menuItemSchema = z.discriminatedUnion("type", [
  headingItemSchema,
  foodItemSchema,
  drinkItemSchema,
  otherItemSchema,
]);

const preOrderBaseItemParser = z.object({
  id: z.string(),
  menu_id: z.string(),
  name: z.string().optional(),
  quantity: z.number().nonnegative(),
  total: z.number().nonnegative(),
  discount_value: z.number().nonnegative(),
  type: z.string(),
  sub_type: z.string().optional(),
  type_id: z.string().optional(),
  sub_type_id: z.string().optional(),
  // The following fields are for legacy/compatibility, but are not required for new items
  price: z.number().optional(),
  base_price: z.number().optional(),
  options_price: z.number().optional(),
  diet_types: z.array(z.string()).optional(),
  allergens: z.array(z.string()).optional(),
  configurable_options: z.array(configurableOptionSchema).optional(),
  // New field for selected options
  options: z
    .array(
      z.object({
        label: z.string(),
        values: z.array(
          z.object({
            label: z.string(),
            price_change: z.number().optional(),
          }),
        ),
      }),
    )
    .optional(),
  for_name_index: z.number().optional(),
  for_name: z.string().optional(),
});

// Some type trickery to express the recursive PreOrderItem structure
type PreOrderBaseItem = z.infer<typeof preOrderBaseItemParser> & { items?: PreOrderBaseItem[] };

/* PreOrderItem */
export const preOrderItemParser: z.ZodType<PreOrderBaseItem> = preOrderBaseItemParser.extend({
  items: z.array(z.lazy(() => preOrderBaseItemParser)).optional(),
});

/**
 * A pre-order close date as Collins reports it: a UTC timestamp without an
 * offset (e.g. `2026-10-29T22:00:00`), or a date-only `YYYY-MM-DD`. The date is
 * display detail only, so an unparseable value is dropped rather than failing
 * the whole pre-order, and the guest is simply not shown a deadline.
 */
const preorderCloseDateParser = z
  .union([z.iso.date(), z.iso.datetime({ local: true, offset: true })])
  .optional()
  .catch(undefined);

/* Customer PreOrder */
export const customerPreOrderParser = z.object({
  totalCost: z.number().optional(),
  totalDiscount: z.number().optional(),
  requiredPaymentAmount: z.number().optional(),
  serviceChargePercentage: z.number().optional(),
  serviceChargeAmount: z.number().optional(),
  totalCostWithService: z.number().optional(),
  email: z.string().optional(),
  // Tax fields (tax-exclusive venues)
  tax_inclusive: z.boolean().optional(),
  tax_label: z.string().optional(),
  tax_breakdown: z.array(taxBreakdownEntryParser).optional(),
  total_tax: z.number().optional(),
  total_net: z.number().optional(),
  total_gross: z.number().optional(),
  requiredPaymentTax: z.number().optional(),
  requiredPaymentGross: z.number().optional(),
  requiredPaymentTaxBreakdown: z.array(taxBreakdownEntryParser).optional(),
  bookingTypeName: z.string().optional(),
  booking: z.object({
    _id: z.string(),
    date: z.string(),
    venue_id: z.string(),
    venue: z.object({
      _id: z.string(),
      title: z.string(),
      photo: z.union([z.string(), z.boolean()]),
      country_code: z.string().optional(),
      currency: z.string().optional(),
      preorder_items: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          price: z.union([z.number(), z.string()]),
          description: z.string().optional(),
          type: z.string().optional(),
          sub_type: z.string().optional(),
        }),
      ),
    }),
    num_people: z.number().nonnegative(),
    type: z.string().optional(),
    additional_types: z.array(bookingAdditionalTypeParser).optional(),
  }),
  menus: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      fixed_price: z.boolean().optional(),
      fixed_price_rules: z
        .array(
          z.object({
            max_items: z.number(),
            price: z.number(),
          }),
        )
        .optional(),
      description: z.string().optional(),
      item_multiplier: z.number().optional(),
      max_per_person: z.number().optional(),
      min_items: z.number().optional(),
      max_items: z.number().optional(),
      discount_amount: z.number().optional(),
      hide_prices: z.boolean().optional(),
      max_per_sub_type: z.number().optional(),
      minimum_preorder_amount: z.number().optional(),
      minimum_preorder_amount_per: z.string().optional(),
      items: z.array(menuItemSchema),
      booking_type: z.string().optional(),
    }),
  ),
  items: z.array(preOrderItemParser),
  pendingPreorderId: z.string().optional(),
  dietaryRequirements: z
    .array(
      z.object({
        name: z.string().optional(),
        diet_types: z.array(z.string()).optional(),
        allergies: z.array(z.string()).optional(),
        notes: z.string().optional(),
      }),
    )
    .optional(),
  customer_preorder_settings: z
    .object({
      complete: z.boolean(),
      created_date: z.string(),
      close_date: preorderCloseDateParser,
      menus: z.array(
        z.object({
          menu_id: z.string(),
          who_pays: z.string().optional(),
          payment_required: z.string(),
          item_multiplier: z.number().optional(),
          max_per_person: z.number().optional(),
          min_items: z.number().optional(),
          max_items: z.number().optional(),
          discount_amount: z.number().optional(),
          discount_type: z.string().optional(),
          hide_prices: z.boolean().optional(),
        }),
      ),
      has_menu_restrictions: z.boolean(),
    })
    .optional(),
  /**
   * When true the venue has disabled the shareable pre-order link, so the guest
   * must not be offered multi-person ordering. Collins resolves this from the
   * booking type and reports it at the top level of the customer pre-order
   * payload, not inside `customer_preorder_settings`.
   */
  disable_shareable_link: z.boolean().optional(),
  hide_dietary_requirements: z.union([z.boolean(), z.null()]).optional(),
  num_group_member_orders: z.number().optional(),
  menu_counts: z
    .union([
      z.record(
        z.string(),
        z.object({
          menu_id: z.string(),
          total_items: z.number(),
        }),
      ),
      z.array(
        z.object({
          menu_id: z.string(),
          total_items: z.number(),
        }),
      ),
    ])
    .optional(),
  forNames: z.array(z.string()).optional(),
  pending: z.boolean().optional(),
});

/**
 * Covers as Collins reports them. Occasionally a numeric string rather than a
 * number, so both are accepted and normalised to a number.
 */
export const collinsCoversParser = z.union([z.number(), z.string().regex(/^\d+$/).transform(Number)]);

/**
 * A booking date as Collins reports it, normalised to `YYYY-MM-DD`.
 *
 * Accepts the ISO 8601 string and the legacy
 * `{ date, timezone_type, timezone }` object, both of which the check-deposit
 * payload is known to send.
 */
export const collinsBookingDateParser = z.union([
  z.string().transform((dateStr) => dateStr.split("T")[0]),
  z
    .object({
      date: z.string(),
      timezone_type: z.number(),
      timezone: z.string(),
    })
    .transform((dateObj) => dateObj.date.split(" ")[0]),
]);

/**
 * A booking time as Collins reports it.
 *
 * Collins copies the booking's own `time` onto the check-deposit payload, and a
 * booking type that captures no time sends null. The time is display detail
 * only, so it must never fail the parse: a rejection is returned to the guest
 * as `successful: false`, leaving someone who has already paid polling until
 * the 120s "Payment Taking Too Long" timeout.
 *
 * Normalised to an empty string so the consumers' existing `booking.time && …`
 * guards read a missing time as "no time to show", and the booking keeps a
 * plain `string` for every caller that formats it.
 */
export const collinsBookingTimeParser = z
  .string()
  .nullish()
  .transform((time) => time ?? "");

/**
 * A booking created alongside the primary one for a linked booking (e.g. brunch
 * then lunch). Shape as returned by `/checkDeposit`, mirroring that payload's
 * camelCase convention.
 *
 * Every field tolerates being absent *and* being the wrong type: a linked
 * booking is display-only detail, so it must never be able to invalidate the
 * surrounding deposit check and leave a guest who has paid waiting for a
 * confirmation that never arrives. Fields that cannot be understood are dropped
 * individually so the rest of the entry still shows.
 */
export const additionalBookingParser = z.looseObject({
  bookingId: z.string().optional().catch(undefined),
  reference: z.string().optional().catch(undefined),
  typeName: z.string().nullish().catch(undefined),
  date: collinsBookingDateParser.optional().catch(undefined),
  time: z.string().optional().catch(undefined),
  // Null for booking types that do not capture covers (e.g. drinks-only).
  numPeople: collinsCoversParser.nullish().catch(undefined),
});

/**
 * A booking created alongside the primary one, as returned by `/submitBooking`.
 * Collins returns the full booking document here, so the naming differs from
 * {@link additionalBookingParser}; both are normalised for display by the frontend.
 *
 * Tolerant for the same reason: an unparseable field must degrade to "not shown"
 * rather than escaping validation as a raw value the display code cannot format.
 */
export const submittedAdditionalBookingParser = z.looseObject({
  _id: z.string().optional().catch(undefined),
  reference: z.string().optional().catch(undefined),
  date: collinsBookingDateParser.optional().catch(undefined),
  time: z.string().optional().catch(undefined),
  num_people: collinsCoversParser.nullish().catch(undefined),
  type: z
    .looseObject({ name: z.string().optional().catch(undefined) })
    .optional()
    .catch(undefined),
});

/**
 * Wrap a linked-booking entry parser into a resilient list.
 *
 * Entries are parsed individually and any that cannot be understood are dropped,
 * so one malformed linked booking neither hides the others nor invalidates the
 * response it arrived in. A payload where the list itself is not an array is
 * dropped altogether.
 */
function resilientAdditionalBookings<entry extends z.ZodType>(entryParser: entry) {
  return (
    z
      .array(z.unknown())
      .optional()
      .transform((entries) => {
        if (entries === undefined) return undefined;
        const parsed: z.infer<entry>[] = [];
        for (const entry of entries) {
          const result = entryParser.safeParse(entry);
          if (result.success) {
            parsed.push(result.data);
          }
        }
        return parsed;
      })
      .catch(undefined)
      // Keeps the key itself optional on the surrounding object.
      .optional()
  );
}

/**
 * The linked bookings of a `/checkDeposit` booking payload, parsed resiliently.
 */
export const additionalBookingsParser = resilientAdditionalBookings(additionalBookingParser);

/**
 * A partner `return_url`. Only http(s) is accepted, so a `javascript:` or
 * `data:` URL can never become a redirect or a form action.
 */
export const partnerReturnUrlParser = z.url({ protocol: /^https?$/ }).max(2048);
export const partnerReturnMethodParser = z.enum(["get", "post"]);

/**
 * The deposit fields a partner callback carries: what identifies the deposit,
 * what it was for and how much, how it was paid, and DMN's computed
 * `net_amount`/`isPaid`/`isAuth`. A callback can travel on a query string into
 * browser history and partner logs, so this is an allowlist, matching Collins'
 * `LegacyBookingSummary::DEPOSIT_FIELDS`: card metadata, free-text notes, the
 * staff user, receipt and integration state, and any field added later are
 * never sent, whichever path the booking came from.
 */
const LEGACY_CALLBACK_DEPOSIT_FIELDS = new Set([
  "_id",
  "id",
  "type",
  "status",
  "amount",
  "gross_amount",
  "tax_amount",
  "tax_inclusive",
  "currency",
  "date",
  "paid_date",
  "paid_type",
  "payment_ref",
  "refunds",
  "net_amount",
  "isPaid",
  "isAuth",
]);

const legacyCallbackDepositParser = z
  .record(z.string(), z.json())
  .transform((deposit) =>
    Object.fromEntries(Object.entries(deposit).filter(([key]) => LEGACY_CALLBACK_DEPOSIT_FIELDS.has(key))),
  );

const legacyCallbackTextParser = z.string().optional().catch(undefined);
const legacyCallbackJsonParser = z.json().optional().catch(undefined);

/**
 * The booking as legacy DMN `/book` sent it to a partner `return_url`: DMN's
 * guest-facing booking summary, `type` without its `value`, and every deposit,
 * less the guest's personal details (see `legacyCallbackGuestParser`).
 *
 * DMN's `/submitBooking` response carries it in-page; after a Stripe payment
 * Collins' check-deposit returns it as `legacyBooking`. Each field is read on
 * its own so one unreadable value never costs the rest.
 */
export const legacyCallbackSummaryParser = z.object({
  _id: legacyCallbackTextParser,
  created_date: legacyCallbackTextParser,
  created_by: legacyCallbackTextParser,
  last_updated: legacyCallbackTextParser,
  reference: legacyCallbackTextParser,
  venue_id: legacyCallbackTextParser,
  date: legacyCallbackTextParser,
  time: legacyCallbackTextParser,
  type: z.object({ id: z.string(), name: z.string().optional() }).catchall(z.json()).optional().catch(undefined),
  num_people: z.number().optional().catch(undefined),
  source: legacyCallbackTextParser,
  packages: legacyCallbackJsonParser,
  offer: legacyCallbackJsonParser,
  deposits: z.array(legacyCallbackDepositParser).optional().catch(undefined),
});

/** The guest's details in legacy DMN's callback booking, sent only by POST. */
export const legacyCallbackGuestParser = z.object({
  first_name: legacyCallbackTextParser,
  last_name: legacyCallbackTextParser,
  email: legacyCallbackTextParser,
  notes: legacyCallbackTextParser,
});

export type LegacyCallbackSummary = z.infer<typeof legacyCallbackSummaryParser>;
export type LegacyCallbackGuest = z.infer<typeof legacyCallbackGuestParser>;

export const bookingParser = z.object({
  bookingId: z.string(),
  bookingRef: z.string(),
  /** Other bookings created as part of a linked booking. Absent when not linked. */
  additionalBookings: additionalBookingsParser,
  status: z.string().optional(),
  depositTotal: z.string().nullable(), // eg '£20.00'
  guestCount: z.number().nonnegative(),
  time: collinsBookingTimeParser,
  date: z.any(),
  venue: z.object({
    title: z.string(),
    currency: z.string(),
    collins_features: z.union([z.array(z.string()), z.record(z.string(), z.any())]).optional(),
    country_code: z.string().nullable(),
    timezone: z.string().optional(),
    // Nullish for the same reason as `venueConfigParser.photo`: Collins sends
    // null for a venue with no image, and it must not fail the booking payload.
    photo: z.string().nullish(),
    hideDietaryRequirements: z.boolean().optional().default(false),
    email_logo: z
      .object({
        thumbnail: z.string().optional(),
      })
      .nullish(),
  }),
  paymentProcessor: z.string(),
  depositTypePayment: z.union([z.boolean(), z.literal("true"), z.literal("false")]), // Yuck. presumably API legacy reason for this string-boolean
  deposits: z.array(z.string()).optional(), // Array of deposit IDs;
  customerName: z.array(z.string()).optional(), // Array of deposit IDs;
  customerNotes: z.array(z.string()).optional(), // Array of customer notes;
  customer_preorder_settings_menus: z
    .array(
      z.object({
        menu_id: z.string(),
        hide_prices: z.boolean(),
      }),
    )
    .optional(),
  user: z
    .object({
      user_id: z.string(),
      user_name: z.string(),
    })
    .optional(),
  bookingDetails: z
    .object({
      first_name: z.string().nullable().optional(),
      last_name: z.string().nullable().optional(),
      email: z.string().nullable().optional(),
      phone: z.string().nullable().optional(),
      country_code: z.string().optional(),
      notes: z.string().optional(),
    })
    .optional(),
  bookingTypeName: z.string().optional(),
  /**
   * When true the venue refuses credit-funded cards for this booking's type, so
   * the guest must pay by debit card. Collins enforces this server-side on every
   * chargeable, so carrying it here only lets the guest be warned first.
   * Optional because it is absent until DMN emits it.
   */
  disableCreditCardPayments: z.boolean().optional(),
  /**
   * The venue's deposit terms, carried through so the confirmation can repeat
   * what the guest agreed to at the payment step. Display text only.
   */
  depositTerms: z.string().optional(),
  /**
   * The deposit the guest was actually charged, as configured by the venue at
   * booking time. Present only when money was taken (a `payment` deposit), so
   * the confirmation can show it without deriving it from the totals.
   */
  depositPaidAmount: z.number().positive().optional(),
  /**
   * Whether the venue takes its deposit alongside a paid pre-order. When false,
   * paying for a pre-order replaces the deposit, so nothing was charged for it
   * and it must not be listed on the confirmation.
   */
  depositTakenWithPreorder: z.boolean().optional(),
  /**
   * Title of the offer the guest added to the booking, recovered from the
   * cached `/book` response. Collins' check-deposit payload does not report the
   * offer, so without this the confirmation cannot show what they chose.
   */
  selectedOffer: z.string().optional(),
  /**
   * Reward figures recovered from the cached `/book` response so the /confirm
   * page can show the rewards banner. Collins' check-deposit response carries
   * no reward data of its own.
   */
  rewards: z
    .object({
      points: z.coerce.number(),
      pointsValue: z.coerce.number(),
      redeemMinimumAmount: z.coerce.number().optional(),
      redeemMinimumValue: z.coerce.number().optional(),
    })
    .optional(),
  /** Booking source, needed to decide reward eligibility on /confirm. */
  source: z.string().optional(),
  /** Affiliate id. Affiliate bookings never earn reward points. */
  affiliateId: z.string().optional(),
  /** Newsletter preference captured at submission, replayed into sign-up. */
  newsletterSignup: z.boolean().optional(),
  /** Venue of the paid `/book` booking, recovered from the cached pending booking. */
  venueId: z.string().optional(),
  /** Booking type the guest paid for, recovered from the cached pending booking. */
  typeId: z.string().optional(),
  /** Duration in minutes the guest paid for, recovered from the cached pending booking. */
  duration: z.number().optional(),
  /** The partner `return_url` bound to the pending booking before payment. */
  returnUrl: partnerReturnUrlParser.optional(),
  returnMethod: partnerReturnMethodParser.optional(),
  /**
   * The booking as legacy DMN sent it to a partner `return_url`, from Collins'
   * check-deposit. Only the partner redirect reads it, so an unreadable one is
   * dropped rather than failing the booking.
   */
  legacyBooking: legacyCallbackSummaryParser.optional().catch(undefined),
});

export const updateCustomerPreOrderItems = z.object({
  booking_id: z.string(),
  pendingPreorderId: z.string().nullable(),
  items: z.array(preOrderItemParser),
  booking: customerPreOrderParser.shape.booking,
  booking_type: z.string().optional(),
});

export const stripeInitialiseResponseParser = z.object({
  payment_intent: z.object({
    client_secret: z.string(),
    id: z.string(),
  }),
  require_3DS: z.boolean().optional(),
});

export const stripeFinaliseResponseParser = z.object({
  successful: z.boolean(),
});

export type StripeInitialiseResponse = z.infer<typeof stripeInitialiseResponseParser>;
export type StripeFinaliseResponse = z.infer<typeof stripeFinaliseResponseParser>;

export type ManageOrderData = z.infer<typeof manageOrderDataParser>;
export type CustomerOrder = z.infer<typeof customerOrderParser>;
export type PreorderSettings = z.infer<typeof preorderSettingsParser>;
export type MenuSettings = z.infer<typeof menuSettingsParser>;

export const orderDietaryRequirementParser = z.object({
  name: z.string().optional(),
  diet_types: z.array(z.string()),
  allergies: z.array(z.string()),
});
export type OrderDietaryRequirement = z.infer<typeof orderDietaryRequirementParser>;

export const customerOrderParser = z.object({
  id: z.string(),
  name: z.string(),
  created_date: z.string(),
  status: z.enum(["pending", "complete", "deleted"]),
  payment_ref: z.string().optional(),
  amount_paid: z.number().optional(),
  total_cost: z.number(),
  items: z.array(preOrderItemParser),
  leadBookerPaidAmount: z.number().optional(),
  groupMemberPaidAmount: z.number().optional(),
  service_charge_percentage: z.number().optional(),
  service_charge_amount: z.number().optional(),
  total_cost_with_service: z.number().optional(),
  customer_notes: z.string().optional(),
  dietary_requirements: z.array(orderDietaryRequirementParser).optional(),
  // Per-order tax fields (tax-exclusive venues)
  tax_inclusive: z.boolean().optional(),
  tax_label: z.string().optional(),
  tax_breakdown: z.array(taxBreakdownEntryParser).optional(),
  total_net: z.number().optional(),
  total_gross: z.number().optional(),
});

export const menuSettingsParser = z.object({
  menu_id: z.string(),
  who_pays: z.string().optional(),
  payment_required: z.string(),
  item_multiplier: z.number().optional(),
  max_per_person: z.number().optional(),
  min_items: z.number().optional(),
  max_items: z.number().optional(),
  discount_amount: z.number().optional(),
  discount_type: z.string().optional(),
  hide_prices: z.boolean().optional(),
});

export const preorderSettingsParser = z.object({
  complete: z.boolean(),
  close_date: preorderCloseDateParser,
  has_menu_restrictions: z.boolean(),
  menus: z.array(menuSettingsParser),
  min_group_member_orders: z.number().optional(),
  max_group_member_orders: z.number().optional(),
  current_group_member_orders: z.number().optional(),
});

export const manageOrderDataParser = z.object({
  orders: z.array(customerOrderParser),
  serviceChargePercentage: z.number(),
  totalCost: z.number(),
  totalCostWithService: z.number(),
  totalDiscount: z.number(),
  totalServiceCharge: z.number(),
  serviceChargeAmount: z.number(),
  requiredPaymentAmount: z.number(),
  leadBookerPaidAmount: z.number(),
  groupMemberPaidAmount: z.number(),
  preorderSettings: preorderSettingsParser,
  // Aggregated tax fields (tax-exclusive venues)
  aggregated_tax_breakdown: z.array(taxBreakdownEntryParser).optional(),
  aggregated_total_tax: z.number().optional(),
  tax_inclusive_orders: z.boolean().optional(),
  tax_config: z.object({ tax_label: z.string() }).optional(),

  menuCounts: z.record(
    z.string(),
    z.object({
      menu_id: z.string(),
      total_items: z.number(),
    }),
  ),
  booking: customerPreOrderParser.shape.booking,
});

/* ─── Booking Creation Flow Types ─── */

export const bookingTypeRulesParser = z.object({
  booking_available: z.boolean().optional(),
  bookings_from: z.string().optional(),
  bookings_to: z.string().optional(),
  min_people: z.number().optional(),
  max_people: z.number().optional(),
  min_duration: z.number().optional(),
  max_duration: z.number().optional(),
  override: z.boolean().optional(),
  /** Time at which same-day bookings are shut, e.g. "18:00" or "-60mins". */
  bookings_shut: z.union([z.string(), z.boolean()]).optional(),
  /** Minimum minutes before a same-day booking can be placed. */
  same_day_bookings_min_minutes: z.number().optional(),
});

export const bookingTypeParser = z.object({
  id: z.string(),
  name: z.string(),
  guestlist: z.boolean().optional(),
  value: z.number().optional(),
  private_hire: z.boolean().optional(),
  use_standing_capacity: z.boolean().optional(),
  archived: z.boolean().optional(),
  no_duration: z.boolean().optional(),
  advance_bookings_min_days: z.number().optional(),
  advance_bookings_max_days: z.number().optional(),
  booking_rules: z.record(z.string(), bookingTypeRulesParser).optional(),
  booking_overrides: z
    .array(
      z.object({
        id: z.string().optional(),
        date_from: z.string(),
        date_to: z.string(),
        booking_available: z.boolean().optional(),
        bookings_from: z.string().optional(),
        bookings_to: z.string().optional(),
        min_duration: z.number().optional(),
        max_duration: z.number().optional(),
        min_people: z.number().optional(),
        max_people: z.number().optional(),
        bookings_shut: z.union([z.string(), z.boolean()]).optional(),
        auto_confirm_max: z.number().optional(),
        booking_notes: z.string().optional(),
      }),
    )
    .optional(),
  num_people_overrides: z
    .array(
      z.object({
        num_people_from: z.number(),
        num_people_to: z.number(),
        booking_available: z.boolean().optional(),
        min_duration: z.number().optional(),
        max_duration: z.number().optional(),
        min_people: z.number().optional(),
        max_people: z.number().optional(),
      }),
    )
    .optional(),
  booking_rules_dmn: z.record(z.string(), bookingTypeRulesParser).optional(),
  /** Interval in minutes between selectable time slots (defaults to 15). */
  time_interval: z.number().optional(),
  /** Interval in minutes between selectable duration steps (defaults to 15). */
  duration_interval: z.number().optional(),
  all_names_required: z.boolean().optional(),
  all_emails_required: z.boolean().optional(),
  all_phone_required: z.boolean().optional(),
  gender_required: z.boolean().optional(),
  email_required: z.boolean().optional(),
  phone_required: z.boolean().optional(),
  customer_preorder_settings: z
    .object({
      payment_required: z.string(),
      service_charge_percentage: z.number(),
      payment_percentage: z.number(),
      /**
       * When true the venue has disabled the shareable pre-order link for this
       * booking type, so the guest must not be offered multi-person ordering.
       * Collins only emits the field when it is enabled, hence optional.
       */
      disable_shareable_link: z.boolean().optional(),
    })
    .nullable()
    .optional(),
  /**
   * When true the venue refuses credit-funded cards for this booking type, so the
   * guest must pay by debit card. Collins only emits the field when it is enabled,
   * hence optional rather than defaulted to false.
   */
  disable_credit_card_payments: z.boolean().optional(),
});

/**
 * Single custom_details entry — one per venue group — containing
 * marketing preference opt-in / opt-out history.
 */
export const customDetailsEntryParser = z.object({
  owner_id: z.string().optional(),
  marketing_preferences: z
    .array(
      z.object({
        id: z.string(),
        opt_in_date: z.unknown().nullish(),
        opt_out_date: z.unknown().nullish(),
      }),
    )
    .optional(),
});

/**
 * Shared Zod schema for user custom_details.
 * The Collins API may return either a single object or an array of objects
 * depending on the endpoint, so we accept both shapes via z.union.
 */
export const customDetailsParser = z.union([z.array(customDetailsEntryParser), customDetailsEntryParser]).nullish();

export const bookingTagParser = z.object({
  id: z.string(),
  label: z.string(),
  type: z.enum(["text", "longtext", "select", "multiselect", "boolean"]),
  options: z.array(z.string()).optional(),
  mandatory: z.boolean().optional(),
  archived_at: z.any().nullable(),
  booking_types: z.array(z.string()).optional(),
});

export const venueConfigParser = z.object({
  venueId: z.string(),
  venueGroup: z.string().optional(),
  region: z.string().optional(),
  title: z.string(),
  currency: z.string(),
  currencySymbol: z.string(),
  bookingTypes: z.array(bookingTypeParser),
  bookingTags: z.array(bookingTagParser).optional(),
  tagBookingTypes: z.record(z.string(), z.array(z.string())).optional(),
  openingTimes: z.record(
    z.string(),
    z.union([
      z.array(
        z.object({
          open: z.string(),
          close: z.string(),
          status: z.boolean().optional(),
          private_hire: z.boolean().optional(),
        }),
      ),
      z.object({
        open: z.string(),
        close: z.string(),
        status: z.boolean().optional(),
        private_hire: z.boolean().optional(),
      }),
    ]),
  ),
  bookingsShut: z
    .record(
      z.string(),
      z.object({
        time: z.string().optional(),
        message: z.string().optional(),
      }),
    )
    .optional(),
  venueBookingUnavailable: z
    .record(
      z.string(),
      z.array(
        z.object({
          unavailable_from: z.string(),
          unavailable_to: z.string(),
          unavailable_from_time: z.string().optional(),
          unavailable_to_time: z.string().optional(),
          unavailable_message: z.string().optional(),
        }),
      ),
    )
    .optional(),
  paymentsEnabled: z.boolean().optional(),
  authsEnabled: z.boolean().optional(),
  corporateCardFee: z.number().optional(),
  venueHasCorporateFeeDisabled: z.boolean().optional(),
  venueGroupHasSodaIntegration: z.boolean().optional(),
  marketing_default_opt_in: z.boolean().optional(),
  marketingPreferences: z
    .array(
      z.object({
        id: z.string(),
        description: z.string(),
      }),
    )
    .optional(),
  enableApplePay: z.union([z.boolean(), z.literal("true"), z.literal("false")]).optional(),
  enableGooglePay: z.union([z.boolean(), z.literal("true"), z.literal("false")]).optional(),
  enableMobilePaymentMethods: z.boolean().optional(),
  collins_features: z.union([z.array(z.string()), z.record(z.string(), z.any())]).optional(),
  // Venue imagery for the /book sidebar banner. Declared so the values survive
  // the parser rather than being stripped as unknown keys. Named to match
  // `bookingParser.venue` so both routes expose the venue image identically.
  // `photo` is the first entry of the venue's own `photos` array at the 620px
  // `medium` size (see Venue::getBookingSidebarPhoto in Collins).
  // Collins may return protocol-relative URLs (e.g. `//static.designmynight.com/...`),
  // so these must never be passed through `assetUrl()`.
  // Nullish, not optional: a venue with no photo comes back as `photo: null`, and
  // decoration must never fail the whole response. `resolveVenueImageUrl` already
  // treats anything that is not a non-blank string as absent.
  photo: z.string().nullish(),
  email_logo: z
    .object({
      thumbnail: z.string().optional(),
    })
    .nullish(),
});

export const alternativeVenueTimeSlotParser = z.object({
  start: z.string(),
  available: z.boolean(),
});

export const alternativeVenueTimeParser = z.object({
  venue: z
    .object({
      _id: z.string(),
      title: z.string(),
      street: z.string().optional(),
      postcode: z.string().optional(),
    })
    .optional(),
  slots: z.array(alternativeVenueTimeSlotParser).optional(),
  distance: z.number().optional(),
  booking: z
    .object({
      type: z.union([z.string(), z.object({ id: z.string(), name: z.string() })]),
    })
    .optional(),
});

export const availabilityParser = z.object({
  status: z.string().optional(),
  action: z.enum(["accept", "enquire", "reject", "suggest_times", "may_enquire"]),
  data: z
    .object({
      alternative_times: z
        .array(
          z.object({
            start: z.string(),
            end: z.string(),
            available: z.boolean(),
          }),
        )
        .optional(),
      alternative_venue_times: z.array(alternativeVenueTimeParser).optional(),
      no_availability_action: z.string().optional(),
    })
    .optional(),
});

/** A single item within a preorder menu returned by the booking options API. */
export const preorderMenuItemParser = z.object({
  id: z.string(),
  name: z.string(),
  price: z.number(),
  description: z.string().optional(),
  sub_type: z.string().optional(),
  configurable_options: z.array(configurableOptionSchema).optional(),
  diet_types: z.array(z.string()).optional(),
  // Declared so the RPC output parser does not strip the allergens Collins sends.
  // Without them the /book pre-order step cannot mark items unsuitable for a
  // guest's allergies the way /place-your-order does.
  allergens: z.array(z.string()).optional(),
  // Energy content in kilocalories. Declared for the same reason: the /book
  // menu falls back to these raw items for linked bookings, which do not fetch
  // the server-processed menu data. Caught rather than merely optional so an
  // unset `null` from Collins cannot fail the whole booking-options response.
  calories: z.number().optional().catch(undefined),
});

/** A heading item within a preorder menu (section divider). */
export const preorderMenuHeadingItemParser = z.object({
  type: z.literal("heading"),
  value: z.string(),
});

export type PreorderMenuHeadingItem = z.infer<typeof preorderMenuHeadingItemParser>;

/** A preorder menu grouping items for a booking type. */
export const preorderMenuParser = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  items: z.array(z.union([preorderMenuHeadingItemParser, preorderMenuItemParser])),
  minimum_preorder_amount: z.number().optional(),
  minimum_preorder_amount_per: z.string().optional(),
  limit_preorders: z
    .object({
      type: z.string(),
      min: z.number(),
      max: z.number(),
    })
    .optional(),
  fixed_price: z.boolean().optional(),
  fixed_price_rules: z
    .array(
      z.object({
        max_items: z.number(),
        price: z.number(),
      }),
    )
    .optional(),
  max_per_sub_type: z.number().optional(),
  booking_type: z.string().optional(),
});

export type PreorderMenuItem = z.infer<typeof preorderMenuItemParser>;
export type PreorderMenuItemOrHeading = z.infer<typeof preorderMenuParser>["items"][number];
export type PreorderMenu = z.infer<typeof preorderMenuParser>;

export const depositRequiredParser = z.object({
  total: z.number(),
  type: z.enum(["payment", "auth", "authenticate"]),
  terms: z.string().optional(),
  takeWithPreorder: z.boolean().optional(),
  // Tax fields (tax-exclusive venues)
  tax_inclusive: z.boolean().optional(),
  gross_amount: z.number().optional(),
  gross_amount_formatted: z.string().optional(),
  formatted_tax_rates: z.array(depositTaxRateParser).optional(),
  tax_label: z.string().optional(),
});

export const bookingOptionsParser = z.object({
  bookingPolicy: z.string().nullable().optional(),
  guestListNamesRequired: z.boolean().optional(),
  guestListGenderRequired: z.boolean().optional(),
  guestListEmailRequired: z.boolean().optional(),
  guestListPhoneRequired: z.boolean().optional(),
  dobRequired: z.boolean().optional(),
  dobHidden: z.boolean().optional(),
  specialRequestsHidden: z.boolean().optional(),
  noDuration: z.boolean().optional(),
  newsletterSignup: z.boolean().optional(),
  customField: z.string().nullable().optional(),
  depositRequired: depositRequiredParser.nullable().optional(),
  preorders: z.record(z.string(), z.array(preorderMenuParser)).optional(),
  additionalTypes: z.array(z.any()).optional(),
  christmasNote: z.string().nullable().optional(),
  showPreferredContactMethod: z.boolean().optional(),
});

/**
 * A Collins offer id: the 24-character hex Mongo ObjectId that
 * `SiteUtils::isMongoID` requires. The empty string is allowed because a blank
 * offer is how a stored one is cleared.
 *
 * This is a shape check only. Whether an offer belongs to the venue, and
 * whether it is valid for the booking's date, time, party size and type, can
 * only be decided by Collins — a client-supplied id must never be trusted as
 * authorisation. Rejecting malformed input here simply keeps it from reaching
 * the API.
 */
export const offerIdParser = z.union([z.literal(""), z.string().regex(/^[0-9a-f]{24}$/i, "Invalid offer id")]);

export const countryConfigParser = z.object({
  currency: z.string(),
  currency_symbol: z.string(),
  phone_prefix: z.string().optional(),
  country_code: z.string(),
});

export const bookingCreateRequestParser = z.object({
  venue_id: z.string(),
  source: z.string().default("partner"),
  pending_booking_id: z.string().optional(),
  type: z.union([z.string(), z.object({ id: z.string(), name: z.string() })]).optional(),
  num_people: z.number().optional(),
  date: z.string().optional(),
  time: z.string().optional(),
  duration: z.number().optional(),
  affiliate_id: z.string().optional(),
  offer: offerIdParser.optional(),
  locale: z.string().optional(),
  additionalTypes: z.string().optional(),
  venue_group: z.string().optional(),
  vouchers: z
    .array(
      z.object({
        code: z.string(),
        amountToRedeem: z.number(),
      }),
    )
    .optional(),
});

export const bookingCreateResponseParser = z.object({
  booking: z.any(),
  venueConfig: venueConfigParser,
  countryConfig: countryConfigParser,
  user: z.union([z.any(), z.literal(false)]),
  accessToken: z.string(),
  errors: z
    .object({
      errors: z.record(z.string(), z.array(z.string())),
    })
    .nullable()
    .optional(),
  availability: availabilityParser.nullable().optional(),
  offers: z
    .array(
      z.object({
        _id: z.string(),
        title: z.string(),
        description: z.string().optional(),
      }),
    )
    .optional(),
  bookingOptions: bookingOptionsParser.optional(),
  canAutoconfirm: z.boolean().optional(),
  /**
   * Reward points estimated for this booking. Collins builds these with PHP's
   * `number_format()`, so the currency values arrive as strings ("0.50") rather
   * than numbers — they are coerced here so the rest of the app can treat them
   * as numeric. Note the values are always sterling: Collins'
   * `UserTransaction::pointsToCurrency()` hard-codes GBP regardless of the
   * venue's own currency.
   */
  rewards: z
    .object({
      points: z.coerce.number(),
      pointsValue: z.coerce.number(),
      redeemMinimumAmount: z.coerce.number().optional(),
      redeemMinimumValue: z.coerce.number().optional(),
    })
    .optional(),
  /**
   * Despite its name, Collins populates this from the venue's
   * `hide_dietary_requirements` setting, so a truthy value means the allergens
   * and dietary controls must be HIDDEN. Accepts null because the underlying
   * venue field is untyped and may be absent.
   */
  showDietaryRequirements: z.union([z.boolean(), z.null()]).optional(),
  packages: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
      }),
    )
    .optional(),
  paymentProcessorType: z.string().nullable().optional(),
});

export const bookingSubmitRequestParser = z.object({
  additional_types: z.string(),
  pending_booking_id: z.string(),
  source: z.string(),
  tags: z
    .array(
      z.object({
        id: z.string(),
        value: z.union([z.string(), z.array(z.string()), z.boolean()]).nullable(),
        label: z.string(),
        type: z.string(),
      }),
    )
    .optional(),
  venue_id: z.string(),
  venue_group: z.string(),
  assigned_areas: z.array(z.string()),
  region: z.string(),
  date: z.string(),
  time: z.string(),
  duration: z.number().optional(),
  num_people: z.number(),
  type: z.object({
    guestlist: z.boolean(),
    id: z.string(),
    name: z.string(),
    value: z.number(),
    private_hire: z.boolean(),
    use_standing_capacity: z.boolean(),
  }),
  payments: z.array(z.unknown()),
  newsletter_signup: z.boolean().optional(),
  email: z.email(),
  first_name: z.string(),
  last_name: z.string(),
  notes: z.string().optional(),
  phone: z.string(),
  policy_confirm: z.boolean().optional(),
  marketing_preferences: z.record(z.string(), z.boolean()).optional(),
  accessToken: z.string(),
  dob: z.string().optional(),
  offer: offerIdParser.optional(),
  affiliate_id: z.string().optional(),
  locale: z.string().optional(),
  affiliate_data_permission: z.boolean().optional(),
  partner_data_permission: z.boolean().optional(),
  group_members: z
    .array(z.looseObject({ first_name: z.string().optional(), last_name: z.string().optional() }))
    .optional(),
  package: z.string().optional(),
  custom_field_value: z.string().optional(),
  custom_field_label: z.string().optional(),
  preorder: z.record(z.string(), z.unknown()).optional(),
  deposit: z.record(z.string(), z.unknown()).optional(),
  do_enquiry: z.boolean().optional(),
  // Soda voucher codes applied to the booking; forwarded to Collins on submission.
  vouchers: z.array(z.string()).optional(),
});

export const bookingSubmitResponseParser = z.object({
  booking: z.any(),
  bookingStatus: z.enum(["confirmed", "not confirmed"]),
  venue: z.object({
    _id: z.string(),
    title: z.string(),
    currency: z.string(),
    path: z.string().optional(),
  }),
  additionalBookings: resilientAdditionalBookings(submittedAdditionalBookingParser),
});

/* Inferred TypeScript types for booking creation flow */
export type BookingTypeRules = z.infer<typeof bookingTypeRulesParser>;
export type BookingType = z.infer<typeof bookingTypeParser>;
export type BookingTag = z.infer<typeof bookingTagParser>;
export type VenueConfig = z.infer<typeof venueConfigParser>;
export type Availability = z.infer<typeof availabilityParser>;
export type AlternativeVenueTime = z.infer<typeof alternativeVenueTimeParser>;
export type AlternativeVenueTimeSlot = z.infer<typeof alternativeVenueTimeSlotParser>;
export type DepositRequired = z.infer<typeof depositRequiredParser>;
export type BookingOptions = z.infer<typeof bookingOptionsParser>;
export type CountryConfig = z.infer<typeof countryConfigParser>;
export type BookingCreateRequest = z.infer<typeof bookingCreateRequestParser>;
export type BookingCreateResponse = z.infer<typeof bookingCreateResponseParser>;
export type Rewards = NonNullable<BookingCreateResponse["rewards"]>;
export type BookingSubmitRequest = z.infer<typeof bookingSubmitRequestParser>;
export type BookingSubmitResponse = z.infer<typeof bookingSubmitResponseParser>;
